import { config } from '@config'
import { ACTIVITY_HIERARCHY_MAP, DEFAULT_ACTIVITY_HIERARCHY } from '@constants'
import { OrderType } from '@modules/common/models/enums/OrderManagement'
import formatNumberByLocale from '@utils/format-number-by-locale'
import { HttpService } from '@services/HttpService'
import { storage } from '@services/storageService'
import memoize from 'lodash/memoize'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import { ORDER_SERVICE_ENDPOINTS } from '../constants/apiConstants'
import { PRODUCT_SERVICE_ENDPOINTS } from '@modules/showroom/productCollection/enhancedView/constants/cnf-product-constants'

export default class KPIDataSummary {
  private httpService = new HttpService()

  getOptionsHeader = () => {
    const accessToken: string = storage.getItem('access_token')
    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    }
  }

  async fetchHierarchy(bsgId, orderId, orderType, subSection, clientOrderId) {
    if (!orderId) return undefined
    const orderPath =
      orderType === OrderType.Client
        ? `/clientOrderId/${orderId}`
        : `/clientOrderId/${clientOrderId}/doorOrderId/${orderId}`
    let url = `${config.buyingBoardServiceRestEndpoint}${
      ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCTS_HIERARCHY
    }/buyingSessionGroupId/${bsgId}${orderPath}?includeKPIs=true&includeMixKPIs=true&includeDoorKPIs=${
      subSection === 'DoorsKPI'
    }`

    return this.httpService
      .post(
        url,
        {},
        {
          ...this.getOptionsHeader()
        }
      )
      .then(response =>
        this.buildKPIHeirarchy(response.data.data.productHierarchy.categories)
      )
  }

  async fetchBuyKPIHierarchy(bsgId, buyingSessionId, clientId, quantityType: string) {
    let url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_BUY_KPI_PRODUCTS_HIERARCHY}/buyingSessionGroupId/${bsgId}/buyingSession/${buyingSessionId}/client/${clientId}`
    return this.httpService
      .get(url, {
        params: {
          quantityType,
          isShowroomApp: true
        },
        ...this.getOptionsHeader()
      })
      .then(response => response.data.data.productHierarchy.categories)
  }

  async fetchForecastKPIHierarchy(bsgId, buyingSessionId, clientId) {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_FORECAST_KPI_PRODUCTS_HIERARCHY}/buyingSessionGroupId/${bsgId}`
    const response: any = await this.httpService.get(url, {
      params: {
        buyingSessionId,
        clientId,
        isShowroomApp: true
      },
      ...this.getOptionsHeader()
    })
    const forcastKPI = this.buildForecastKPIHierarchy(response?.data?.totals)
    return forcastKPI?.categories
  }

  fetchEnhancedOrderProductHeirarchy = (
    bsgId,
    orderId,
    orderType,
    clientOrderId,
    body
  ) => {
    if (orderType === OrderType.Client) {
      if (!orderId) {
        return undefined
      }
    } else {
      if (!clientOrderId) {
        return undefined
      }
    }

    const orderPath =
      orderType === OrderType.Client
        ? `/clientOrder/${orderId}/multiple-aggregates`
        : `/clientOrder/${clientOrderId}/doorOrder/${orderId}/multiple-aggregates`
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${bsgId}${orderPath}`

    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response?.data)
  }

  fetchEnhancedPlanningBudgetHeirarchy = (
    buyingSessionGroupId: string,
    clientId: string,
    body: any
  ) => {
    const url = `${config.buyingBoardServiceRestEndpoint}core/planningBudget/buyingSessionGroup/${buyingSessionGroupId}/client/${clientId}/aggregates`
    return this.httpService
      .post(url, body, {
        ...this.getOptionsHeader()
      })
      .then(response => response.data)
  }

  buildForecastKPIHierarchy = memoize((forecastKPIData: Array<any>) => {
    let activitiesKPIResponse = []
    const newForecastKPIData = forecastKPIData.map(kpiData => {
      return {
        comboId: kpiData?.comboId,
        name: kpiData?.name || '',
        kpis: {
          aws: kpiData?.avgWeeklySales || 0,
          mixRatioByDraftQty: `${formatNumberByLocale(kpiData?.mixQuantity, 2)}%`,
          mixRatioByDraftValue: `${formatNumberByLocale(kpiData?.mixValue, 2)}%`,
          averageDepth: kpiData?.avgDepth || 0,
          skuEfficiency: kpiData?.skuEFF || 0,
          numberOfStores: kpiData?.numberOfStores || 0,
          totalForecastQuantity: kpiData?.forecastQuantity || 0,
          totalForecastValue: kpiData?.forecastValue || 0,
          salesQty: kpiData?.salesQuantity || 0
        },
        totalProducts: kpiData?.count || 0,
        children: null
      }
    })
    const helper = newForecastKPIData.reduce(
      (h, o) => ({ ...h, [o.comboId]: { ...h[o.comboId], ...o } }),
      Object.create(null)
    )
    activitiesKPIResponse = newForecastKPIData.reduce((t, node) => {
      const current = helper[node.comboId]
      if (current.comboId.split('.').length === 2) {
        current.childrenMeta =
          ACTIVITY_HIERARCHY_MAP[current?.name] || DEFAULT_ACTIVITY_HIERARCHY
        t.push(current)
      } else {
        const split = node.comboId.split('.')
        const x =
          split.length > 2 ? split.slice(0, split.length - 1).join('.') : node.comboId
        helper[x].children || (helper[x].children = [])
        helper[x].children.push(current)
        helper[x].children = sortBy(helper[x].children, 'name')
      }
      return t
    }, [])
    return { categories: activitiesKPIResponse }
  })

  buildKPIHeirarchy = categories => {
    return map(categories, category => {
      Object.keys(category?.kpis?.doorKpis || {}).forEach(door => {
        category.kpis.doorKpis[door] = this.mapMixRatioKPI(category?.kpis?.doorKpis[door])
      })
      return {
        ...category,
        kpis: this.mapMixRatioKPI(category?.kpis),
        children: this.buildKPIHeirarchy(category?.children)
      }
    })
  }

  mapMixRatioKPI = kpi => ({
    ...kpi,
    mixRatioBySizedQty: `${formatNumberByLocale(
      kpi?.mixBySizeQuantity || 0,
      2
    )}% / ${formatNumberByLocale(kpi?.mixTargetQuantity || 0, 2)}%`,
    mixRatioBySizedValue: `${formatNumberByLocale(
      kpi?.mixValueBySizeQuantity || 0,
      2
    )}% / ${formatNumberByLocale(kpi?.mixTargetValue || 0, 2)}%`,
    mixRatioByDraftQty: `${formatNumberByLocale(
      kpi?.mixByDraftQuantity || 0,
      2
    )}% / ${formatNumberByLocale(kpi?.mixTargetQuantity || 0, 2)}%`,
    mixRatioByDraftValue: `${formatNumberByLocale(
      kpi?.mixValueByDraftQuantity || 0,
      2
    )}% / ${formatNumberByLocale(kpi?.mixTargetValue || 0, 2)}%`,
    mixRatioByProductQty: `${formatNumberByLocale(
      kpi?.mixByProductQuantity || 0,
      2
    )}% / ${formatNumberByLocale(kpi?.mixTargetQuantity || 0, 2)}%`,
    mixRatioByProductValue: `${formatNumberByLocale(
      kpi?.mixValueByProductQuantity || 0,
      2
    )}% / ${formatNumberByLocale(kpi?.mixTargetValue || 0, 2)}%`
  })
}
